/**
 * /payroll/contractors/$contractorId — 1099 contractor detail view.
 * Shows contractor info, W-9 status, and payment history.
 */
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";

import { api } from "@/lib/api";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll/contractors_/$contractorId")({
  component: ContractorDetailPage,
});

interface Contractor {
  id: string;
  business_name: string | null;
  first_name: string | null;
  last_name: string | null;
  tax_id_type: string | null;
  address_line1: string | null;
  address_city: string | null;
  address_state: string | null;
  address_zip: string | null;
  w9_on_file: boolean;
  backup_withholding_required: boolean;
  is_active: boolean;
  created_at: string;
}

function displayName(c: Contractor) {
  if (c.business_name) return c.business_name;
  return [c.first_name, c.last_name].filter(Boolean).join(" ") || "—";
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Stack direction="row" sx={{ py: 1, borderBottom: "1px solid", borderColor: "divider" }}>
      <Typography variant="body2" color="text.secondary" sx={{ width: 200, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2">{value ?? "—"}</Typography>
    </Stack>
  );
}

function ContractorDetailPage() {
  const { contractorId } = useParams({ strict: false }) as { contractorId: string };
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: contractor, isLoading, error } = useQuery({
    queryKey: ["payroll-contractors", contractorId],
    queryFn: async () => api<Contractor>(`/api/payroll/contractors/${contractorId}`),
    enabled: !!contractorId,
  });

  const toggleW9Mut = useMutation({
    mutationFn: async (w9_on_file: boolean) =>
      api(`/api/payroll/contractors/${contractorId}`, {
        method: "PATCH",
        body: { w9_on_file },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payroll-contractors", contractorId] }),
  });

  const toggleActiveMut = useMutation({
    mutationFn: async (is_active: boolean) =>
      api(`/api/payroll/contractors/${contractorId}`, {
        method: "PATCH",
        body: { is_active },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payroll-contractors", contractorId] });
      qc.invalidateQueries({ queryKey: ["payroll-contractors"] });
    },
  });

  if (isLoading) return <CircularProgress size={24} sx={{ m: 3 }} />;
  if (error) return <Alert severity="error" sx={{ m: 3 }}>Failed to load contractor.</Alert>;
  if (!contractor) return null;

  return (
    <Box sx={{ p: 3 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate({ to: "/payroll/contractors" })}
        sx={{ mb: 2 }}
      >
        All Contractors
      </Button>

      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={600}>{displayName(contractor)}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "monospace" }}>
            {contractor.id}
          </Typography>
        </Box>
        <Stack direction="row" gap={1} alignItems="center">
          <Chip
            label={contractor.is_active ? "Active" : "Inactive"}
            color={contractor.is_active ? "success" : "default"}
            size="small"
          />
          {contractor.w9_on_file ? (
            <Chip icon={<CheckCircleIcon />} label="W-9 on file" color="success" size="small" />
          ) : (
            <Chip icon={<WarningIcon />} label="W-9 missing" color="warning" size="small" />
          )}
          <HelpHint docPath="payroll-l1/contractor-vs-employee.md" label="Contractor classification help" />
        </Stack>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={600} mb={1}>Contractor Information</Typography>
        <InfoRow label="Business Name" value={contractor.business_name} />
        <InfoRow label="First Name" value={contractor.first_name} />
        <InfoRow label="Last Name" value={contractor.last_name} />
        <InfoRow label="Tax ID Type" value={contractor.tax_id_type} />
        <InfoRow
          label="Address"
          value={
            [contractor.address_line1, contractor.address_city, contractor.address_state, contractor.address_zip]
              .filter(Boolean)
              .join(", ") || "—"
          }
        />
        <InfoRow
          label="Backup Withholding"
          value={
            <Chip
              label={contractor.backup_withholding_required ? "Required" : "Not required"}
              color={contractor.backup_withholding_required ? "warning" : "default"}
              size="small"
            />
          }
        />
        <InfoRow
          label="Member Since"
          value={new Date(contractor.created_at).toLocaleDateString()}
        />
      </Paper>

      <Divider sx={{ mb: 3 }} />

      <Stack direction="row" gap={2} mb={3}>
        <Button
          variant="outlined"
          startIcon={contractor.w9_on_file ? <WarningIcon /> : <CheckCircleIcon />}
          onClick={() => toggleW9Mut.mutate(!contractor.w9_on_file)}
          disabled={toggleW9Mut.isPending}
          color={contractor.w9_on_file ? "warning" : "success"}
        >
          {contractor.w9_on_file ? "Mark W-9 Missing" : "Mark W-9 Received"}
        </Button>
        <Button
          variant="outlined"
          color={contractor.is_active ? "error" : "success"}
          onClick={() => toggleActiveMut.mutate(!contractor.is_active)}
          disabled={toggleActiveMut.isPending}
        >
          {contractor.is_active ? "Deactivate" : "Reactivate"}
        </Button>
      </Stack>

      <Typography variant="subtitle2" fontWeight={600} mb={1}>
        1099 Payment History
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Payment history will appear here once 1099 generation is enabled (MONEY-PAYROLL-1099-001).
      </Alert>
    </Box>
  );
}
